import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";

const C = {
  bg: "#0a0a0f", bg2: "#111118",
  surface: "rgba(255,255,255,0.04)", sh: "rgba(255,255,255,0.07)",
  border: "rgba(255,255,255,0.08)", text: "#f5f5f7",
  sec: "rgba(245,245,247,0.55)", ter: "rgba(245,245,247,0.30)",
  accent: "#0a84ff", green: "#30d158", orange: "#ff9f0a", purple: "#bf5af2",
};
const F = "-apple-system,'SF Pro Display','Helvetica Neue',sans-serif";

// ── Reusable screens ──────────────────────────────────────────────────────────
const ExpiredScreen = () => (
  <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, padding:"2rem", fontFamily:F }}>
    <div style={{ background:"rgba(255,255,255,0.05)", border:`1px solid ${C.border}`, borderRadius:24, padding:"2.5rem 2rem", maxWidth:340, width:"100%", textAlign:"center" }}>
      <div style={{ fontSize:"2.5rem", marginBottom:"1rem" }}>⏱</div>
      <h2 style={{ margin:"0 0 0.5rem", fontSize:"1.1rem", fontWeight:700, color:C.text }}>Sessione scaduta</h2>
      <p style={{ margin:0, fontSize:"0.85rem", color:C.sec, lineHeight:1.6 }}>Scansiona il QR del tavolo per iniziare una nuova sessione.</p>
    </div>
  </div>
);

// ── Card colours for matched pairs ────────────────────────────────────────────
const PAIR_COLORS = ["#0a84ff","#30d158","#bf5af2","#ff9f0a","#ff453a","#64d2ff","#5e5ce6","#ff375f"];

export default function Memory() {
  const { localeTag, tableId } = useParams();
  const navigate = useNavigate();
  const tokenUrl = new URLSearchParams(useLocation().search).get("token");
  const normalizedLocaleTag = (localeTag || "").toLowerCase();

  const [cards, setCards]     = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [victory, setVictory] = useState(false);
  const [tokenScaduto, setTS] = useState(false);
  const [tokenLoading, setTL] = useState(true);

  // ── Token check ──────────────────────────────────────────────────────────
  useEffect(() => {
    const check = async () => {
      try {
        if (!tokenUrl) { setTS(true); setTL(false); return; }
        const snap = await getDoc(doc(db, "locali", normalizedLocaleTag, "tavoli", tableId));
        if (!snap.exists()) { setTS(true); setTL(false); return; }
        const { tokenAttuale } = snap.data() || {};
        if (!tokenAttuale || tokenUrl !== tokenAttuale) setTS(true);
        setTL(false);
      } catch { setTS(true); setTL(false); }
    };
    check();
  }, [tokenUrl, normalizedLocaleTag, tableId]);

  // ── Load images ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (tokenScaduto) return;
    const load = async () => {
      try {
        const snap = await getDocs(query(collection(db, "menu"), where("localeTag", "==", localeTag)));
        const images = [];
        snap.forEach(d => { if (d.data()?.immagineUrl) images.push(d.data().immagineUrl); });
        if (!images.length) { setCards([]); setLoading(false); return; }
        const selected = images.slice(0, 8);
        const shuffled = [...selected, ...selected]
          .map(v => ({ value: v, sort: Math.random() }))
          .sort((a, b) => a.sort - b.sort)
          .map((v, idx) => ({ id: idx, image: v.value }));
        setCards(shuffled);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [localeTag, tokenScaduto]);

  useEffect(() => {
    if (matched.length > 0 && matched.length === cards.length) setVictory(true);
  }, [matched, cards]);

  const restart = () => {
    setFlipped([]); setMatched([]); setMoves(0); setVictory(false);
    setCards(prev => [...prev].map(v => ({ ...v, sort: Math.random() })).sort((a,b)=>a.sort-b.sort).map((v,i)=>({...v,id:i})));
  };

  // ── Conditional returns ──────────────────────────────────────────────────
  if (tokenLoading) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:C.bg, fontFamily:F }}>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"1rem" }}>
        <div style={{ width:32, height:32, border:`2px solid ${C.border}`, borderTop:`2px solid ${C.sec}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
        <p style={{ margin:0, fontSize:"0.82rem", color:C.sec }}>Caricamento…</p>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
  if (tokenScaduto) return <ExpiredScreen />;

  // ── Flip logic (unchanged) ───────────────────────────────────────────────
  const handleFlip = (index) => {
    if (flipped.includes(index) || matched.includes(index) || flipped.length === 2) return;
    const nf = [...flipped, index];
    setFlipped(nf);
    if (nf.length === 2) {
      setMoves(m => m + 1);
      const [i1, i2] = nf;
      if (cards[i1].image === cards[i2].image) setMatched(prev => [...prev, i1, i2]);
      setTimeout(() => setFlipped([]), 800);
    }
  };

  const pairs = cards.length / 2;
  const foundPairs = matched.length / 2;
  const pct = pairs > 0 ? (foundPairs / pairs) * 100 : 0;

  // Assign a colour to each unique image
  const uniqueImages = [...new Set(cards.map(c => c.image))];
  const colorMap = {};
  uniqueImages.forEach((img, i) => { colorMap[img] = PAIR_COLORS[i % PAIR_COLORS.length]; });

  return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", justifyContent:"center", alignItems:"flex-start", padding:"1.5rem 1rem 3rem", fontFamily:F, color:C.text }}>
      <div style={{ maxWidth:520, width:"100%" }}>

        {/* Header */}
        <div style={{ marginBottom:"1.5rem" }}>
          <p style={{ margin:"0 0 0.2rem", fontSize:"0.72rem", color:C.sec, fontWeight:500 }}>Giochi del tavolo</p>
          <h1 style={{ margin:"0 0 0.2rem", fontSize:"1.5rem", fontWeight:700, letterSpacing:"-0.5px" }}>Memory dei Sapori</h1>
          <p style={{ margin:0, fontSize:"0.72rem", color:C.ter }}>Tavolo {tableId} · {localeTag}</p>
        </div>

        {/* Stats */}
        {!loading && cards.length > 0 && (
          <div style={{ display:"flex", gap:"0.6rem", marginBottom:"1rem" }}>
            {[{label:"Coppie trovate", val:`${foundPairs} / ${pairs}`, color:C.green},{label:"Mosse", val:moves, color:C.orange}].map(s => (
              <div key={s.label} style={{ flex:1, background:C.surface, border:`1px solid ${C.border}`, borderRadius:16, padding:"0.85rem 1rem" }}>
                <p style={{ margin:"0 0 0.25rem", fontSize:"0.65rem", color:C.sec, fontWeight:500 }}>{s.label}</p>
                <p style={{ margin:0, fontSize:"1.4rem", fontWeight:700, color:s.color, letterSpacing:"-0.5px", fontVariantNumeric:"tabular-nums", lineHeight:1 }}>{s.val}</p>
              </div>
            ))}
          </div>
        )}

        {/* Progress */}
        {!loading && cards.length > 0 && (
          <div style={{ height:4, borderRadius:99, background:C.border, marginBottom:"1.25rem", overflow:"hidden" }}>
            <div style={{ height:"100%", borderRadius:99, width:`${pct}%`, background:C.green, transition:"width 0.5s ease" }} />
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"1rem", padding:"3rem 0" }}>
            <div style={{ width:32, height:32, border:`2px solid ${C.border}`, borderTop:`2px solid ${C.sec}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
            <p style={{ margin:0, fontSize:"0.82rem", color:C.sec }}>Caricamento carte…</p>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : cards.length === 0 ? (
          <p style={{ color:C.ter, textAlign:"center", padding:"3rem 0" }}>Nessuna immagine nel menù del locale.</p>
        ) : (
          <>
            {/* Hint */}
            <p style={{ margin:"0 0 1rem", fontSize:"0.78rem", color:C.sec }}>Tocca due carte e trova le coppie</p>

            {/* Card grid */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:"1.25rem" }}>
              {cards.map((card, index) => {
                const isFlipped  = flipped.includes(index) || matched.includes(index);
                const isMatched  = matched.includes(index);
                const pairColor  = colorMap[card.image];
                const isDisabled = isFlipped || flipped.length === 2;

                return (
                  <div key={index} onClick={() => handleFlip(index)} style={{ aspectRatio:"1", perspective:600, cursor: isDisabled ? "default" : "pointer" }}>
                    <div style={{
                      width:"100%", height:"100%", position:"relative",
                      transformStyle:"preserve-3d",
                      transition:"transform 0.38s cubic-bezier(0.25,0.46,0.45,0.94)",
                      transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                    }}>
                      {/* Back */}
                      <div style={{
                        position:"absolute", inset:0, borderRadius:14,
                        backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden",
                        background:"rgba(255,255,255,0.06)",
                        border:`1px solid ${C.border}`,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:"1.3rem", color:C.ter,
                      }}>🍽️</div>
                      {/* Front */}
                      <div style={{
                        position:"absolute", inset:0, borderRadius:14,
                        backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden",
                        transform:"rotateY(180deg)",
                        overflow:"hidden",
                        border: isMatched ? `1.5px solid ${pairColor}` : `1px solid ${C.border}`,
                        background: isMatched ? pairColor + "18" : C.bg2,
                      }}>
                        <img src={card.image} alt="food" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:13 }} />
                        {isMatched && (
                          <div style={{ position:"absolute", bottom:5, right:5, width:18, height:18, borderRadius:"50%", background:pairColor, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.6rem", color:"#fff", fontWeight:700 }}>✓</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Victory */}
            {victory && (
              <div style={{ padding:"1rem", background:"rgba(48,209,88,0.1)", border:"1px solid rgba(48,209,88,0.25)", borderRadius:16, marginBottom:"1rem", textAlign:"center" }}>
                <p style={{ margin:"0 0 0.25rem", fontSize:"1rem", fontWeight:700, color:C.green }}>🏆 Perfetto!</p>
                <p style={{ margin:0, fontSize:"0.82rem", color:C.sec }}>Tutte le coppie trovate in {moves} {moves === 1 ? "mossa" : "mosse"}</p>
              </div>
            )}

            {victory && (
              <button onClick={restart} style={{ width:"100%", padding:"0.75rem", background:"rgba(255,255,255,0.06)", border:`1px solid ${C.border}`, borderRadius:12, color:C.text, fontSize:"0.85rem", fontWeight:600, cursor:"pointer", marginBottom:"0.5rem", fontFamily:F, transition:"background 150ms ease" }}
                onMouseEnter={e=>e.currentTarget.style.background=C.sh} onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.06)"}>
                Gioca ancora
              </button>
            )}
          </>
        )}

        <button onClick={() => navigate(`/giochi/${localeTag}/${tableId}?token=${tokenUrl}`)} style={{ width:"100%", padding:"0.7rem", background:"transparent", border:`1px solid ${C.border}`, borderRadius:12, color:C.sec, fontSize:"0.82rem", fontWeight:500, cursor:"pointer", fontFamily:F, transition:"background 150ms ease", marginTop: victory ? 0 : "0.5rem" }}
          onMouseEnter={e=>e.currentTarget.style.background=C.surface} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          ← Torna ai giochi
        </button>

      </div>
    </div>
  );
}
